import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, ExternalLink, X } from 'lucide-react';

import { useClubByStoreId } from '@/lib/api/clubs';

import { WizardOptionCard } from './WizardOptionCard';
import type { QuickstartAnswers } from './types';

interface StepVipClubProps {
    answers: QuickstartAnswers;
    onChange: (next: Partial<QuickstartAnswers>) => void;
    storeId: string;
    /** Called when the merchant clicks "Set up now" — should close the
     *  wizard and route them to the club configuration page. */
    onSetupClub: () => void;
}

function useHasActiveClub(storeId: string): { hasClub: boolean; isLoading: boolean } {
    const { data: club, isLoading } = useClubByStoreId({ storeId });
    return { hasClub: !!club && club.active === true, isLoading };
}

export function StepVipClub({ answers, onChange, storeId, onSetupClub }: StepVipClubProps) {
    const { t } = useTranslation('common');
    const { hasClub, isLoading } = useHasActiveClub(storeId);

    const select = (value: boolean) => onChange({ wantsClub: value });

    // Auto-unselect "yes" if the gate goes away (e.g. club gets deactivated
    // mid-session), and preselect the first AVAILABLE option once the club
    // status finishes loading. Don't act while loading — the query starts
    // undefined, so hasClub is briefly false during the first render even on
    // stores that do have an active club.
    React.useEffect(() => {
        if (isLoading) return;
        if (!hasClub && answers.wantsClub === true) {
            onChange({ wantsClub: false });
            return;
        }
        if (answers.wantsClub === null) {
            onChange({ wantsClub: hasClub });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasClub, isLoading]);

    const renderDisabledReason = () => (
        <div className="flex items-center gap-2">
            <span>{t('funnelQuickstart.vipClub.noClub')}</span>
            <span
                role="link"
                tabIndex={0}
                onClick={(e) => {
                    e.stopPropagation();
                    onSetupClub();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        onSetupClub();
                    }
                }}
                className="text-primary hover:text-primary/80 inline-flex cursor-pointer items-center gap-1 font-medium underline-offset-2 hover:underline"
            >
                {t('funnelQuickstart.vipClub.setUpNow')}
                <ExternalLink className="h-3 w-3" />
            </span>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h3 className="text-lg font-semibold">
                    {t('funnelQuickstart.vipClub.title')}
                </h3>
                <p className="text-muted-foreground text-sm">
                    {t('funnelQuickstart.vipClub.subtitle')}
                </p>
            </div>

            <div className="flex flex-col gap-3">
                <WizardOptionCard
                    title={t('funnelQuickstart.vipClub.yes.title')}
                    description={t('funnelQuickstart.vipClub.yes.description')}
                    icon={<Crown className="h-5 w-5" />}
                    selected={answers.wantsClub === true}
                    disabled={!hasClub}
                    disabledReason={!hasClub && !isLoading ? renderDisabledReason() : undefined}
                    loading={isLoading}
                    onSelect={() => hasClub && select(true)}
                />
                <WizardOptionCard
                    title={t('funnelQuickstart.vipClub.no.title')}
                    description={t('funnelQuickstart.vipClub.no.description')}
                    icon={<X className="h-5 w-5" />}
                    selected={answers.wantsClub === false}
                    onSelect={() => select(false)}
                />
            </div>
        </div>
    );
}
